import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { authMiddleware, optionalAuthMiddleware } from '@/helpers/authMiddleware';
import { requireOrganizerAccess } from '@/helpers/requireAuth.server';
import { prismaClient } from '@/helpers/prismaClient.server';
import {
  AddOrganizerMemberInput,
  AddOrganizerMemberResult,
  GetOrganizerAdminsInput,
  GetOrganizerMemberSummaryInput,
  GetOrganizerMembersInput,
  GetOrganizerPublicMembersInput,
  MemberSearchResults,
  OrganizerMemberSummary,
  OrganizerMembersPage,
  OrganizerPublicMember,
  OrganizerPublicMembersPage,
  RemoveOrganizerMemberInput,
  RemoveOrganizerMemberResult,
  SearchAddableMembersInput,
} from '@/apiTypes/organizerMember';

const PUBLIC_PROFILE_SELECT = {
  id: true,
  displayName: true,
  firstName: true,
  lastName: true,
  avatarUrl: true,
};

/**
 * Returns the paginated, searchable, sortable roster of current members
 * (rows with `leftAt IS NULL`) for an Organizer. Auth-gated to
 * superadmins and OrganizerAdmins of the target Organizer.
 */
export const getOrganizerMembers = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof GetOrganizerMembersInput> => {
      const result = GetOrganizerMembersInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<z.infer<typeof OrganizerMembersPage>> => {
      await requireOrganizerAccess(data.organizerId, context.auth);
      const {
        organizerId,
        page = 1,
        pageSize = 25,
        sortBy = 'joinedAt',
        sortOrder = 'desc',
        search,
      } = data;
      const skip = (page - 1) * pageSize;
      const db = await prismaClient();

      const trimmed = search?.trim() ?? '';
      const profileFilter = trimmed
        ? {
            profile: {
              OR: [
                {
                  firstName: { contains: trimmed, mode: 'insensitive' as const },
                },
                {
                  lastName: { contains: trimmed, mode: 'insensitive' as const },
                },
                {
                  displayName: {
                    contains: trimmed,
                    mode: 'insensitive' as const,
                  },
                },
                { email: { contains: trimmed, mode: 'insensitive' as const } },
              ],
            },
          }
        : {};

      const where = {
        organizerId,
        leftAt: null,
        ...profileFilter,
      };

      const orderBy =
        sortBy === 'name'
          ? [
              { profile: { lastName: sortOrder } },
              { profile: { firstName: sortOrder } },
            ]
          : { createdAt: sortOrder };

      const [members, totalCount] = await db.$transaction([
        db.organizerMember.findMany({
          where,
          skip,
          take: pageSize,
          orderBy,
          include: {
            profile: {
              select: {
                id: true,
                email: true,
                displayName: true,
                firstName: true,
                lastName: true,
                avatarUrl: true,
              },
            },
          },
        }),
        db.organizerMember.count({ where }),
      ]);

      const totalPages = Math.ceil(totalCount / pageSize);
      return {
        members: members.map((m) => ({
          id: m.id,
          joinedAt: m.createdAt,
          profile: m.profile,
        })),
        pagination: {
          page,
          pageSize,
          totalCount,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      };
    },
  );

/**
 * Adds an existing Profile to an Organizer's member roster. Auth-gated
 * to superadmins and OrganizerAdmins of the target Organizer.
 *
 * If the profile previously left (`leftAt` is set) the existing row is
 * restored by clearing `leftAt`, since `(profileId, organizerId)` is
 * unique. Throws `'Profile is already a member'` if already current,
 * `'Profile not found'` if the profile id doesn't exist.
 */
export const addOrganizerMember = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof AddOrganizerMemberInput> => {
      const result = AddOrganizerMemberInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<z.infer<typeof AddOrganizerMemberResult>> => {
      await requireOrganizerAccess(data.organizerId, context.auth);
      const db = await prismaClient();

      const profile = await db.profile.findUnique({
        where: { id: data.profileId },
        select: { id: true },
      });
      if (!profile) throw new Error('Profile not found');

      const existing = await db.organizerMember.findUnique({
        where: {
          profileId_organizerId: {
            profileId: data.profileId,
            organizerId: data.organizerId,
          },
        },
      });

      if (existing && existing.leftAt === null) {
        throw new Error('Profile is already a member');
      }

      const member = existing
        ? await db.organizerMember.update({
            where: { id: existing.id },
            data: { leftAt: null },
          })
        : await db.organizerMember.create({
            data: {
              profileId: data.profileId,
              organizerId: data.organizerId,
            },
          });

      return { id: member.id, joinedAt: member.createdAt };
    },
  );

/**
 * Returns up to 20 profiles matching `query` against name fields
 * (firstName/lastName/displayName, case-insensitive contains) that are
 * NOT already current members of the target Organizer. Profiles who
 * previously left (`leftAt` set) ARE returned so they can be re-added —
 * `addOrganizerMember` restores those rows by clearing `leftAt`.
 *
 * Email is intentionally NOT searchable: it would let any OrganizerAdmin
 * enumerate Wayra users by email substring. Adding non-Wayra-users by
 * email belongs in a separate email-invite flow.
 *
 * Auth: superadmin or OrganizerAdmin of the target Organizer.
 */
export const searchAddableMembers = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof SearchAddableMembersInput> => {
      const result = SearchAddableMembersInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<z.infer<typeof MemberSearchResults>> => {
      await requireOrganizerAccess(data.organizerId, context.auth);
      const db = await prismaClient();

      // The Zod schema enforces `query.trim().min(2)` upstream, but the
      // handler trims defensively too — matches the project pattern in
      // `getOrganizerMembers` and keeps the contains-filter clean.
      const query = data.query.trim();

      const profiles = await db.profile.findMany({
        where: {
          OR: [
            { firstName: { contains: query, mode: 'insensitive' } },
            { lastName: { contains: query, mode: 'insensitive' } },
            { displayName: { contains: query, mode: 'insensitive' } },
          ],
          NOT: {
            memberOf: {
              some: {
                organizerId: data.organizerId,
                leftAt: null,
              },
            },
          },
        },
        orderBy: [{ lastName: 'asc' }, { firstName: 'asc' }],
        take: 20,
        select: {
          id: true,
          displayName: true,
          firstName: true,
          lastName: true,
          avatarUrl: true,
        },
      });

      return profiles;
    },
  );

/**
 * Removes a Profile from an Organizer's member roster by soft-deleting
 * the OrganizerMember row (sets `leftAt = now()`). Throws
 * `'Profile is not a current member'` when the row doesn't exist or
 * already has `leftAt` set, so a stale UI doesn't silently no-op.
 *
 * Self-removal is allowed: an OrganizerAdmin who is also a member can
 * remove themselves from members; their admin status is unaffected.
 *
 * Auth: superadmin or OrganizerAdmin of the target Organizer.
 */
export const removeOrganizerMember = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof RemoveOrganizerMemberInput> => {
      const result = RemoveOrganizerMemberInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    },
  )
  .handler(
    async ({
      data,
      context,
    }): Promise<z.infer<typeof RemoveOrganizerMemberResult>> => {
      await requireOrganizerAccess(data.organizerId, context.auth);
      const db = await prismaClient();

      const existing = await db.organizerMember.findUnique({
        where: {
          profileId_organizerId: {
            profileId: data.profileId,
            organizerId: data.organizerId,
          },
        },
      });

      if (!existing || existing.leftAt !== null) {
        throw new Error('Profile is not a current member');
      }

      const leftAt = new Date();
      const updated = await db.organizerMember.update({
        where: { id: existing.id },
        data: { leftAt },
      });

      return { id: updated.id, leftAt };
    },
  );

/**
 * Public summary of an organizer's people: total unique profile count
 * across admins ∪ active members (admins take precedence on dedup) plus
 * up to 5 most-recently-joined avatars across that union. No auth.
 */
export const getOrganizerMemberSummary = createServerFn({ method: 'GET' })
  .middleware([optionalAuthMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof GetOrganizerMemberSummaryInput> => {
      const result = GetOrganizerMemberSummaryInput.safeParse(data);
      if (!result.success) throw new Error('Invalid input');
      return result.data;
    },
  )
  .handler(
    async ({ data }): Promise<z.infer<typeof OrganizerMemberSummary>> => {
      const db = await prismaClient();

      const { admins, memberCount, recentMembers } = await db.$transaction(
        async (tx) => {
          const admins = await tx.organizerAdmin.findMany({
            where: { organizerId: data.organizerId },
            orderBy: { createdAt: 'desc' },
            select: {
              createdAt: true,
              profileId: true,
              profile: { select: PUBLIC_PROFILE_SELECT },
            },
          });
          const adminProfileIds = admins.map((a) => a.profileId);
          const memberWhere = {
            organizerId: data.organizerId,
            leftAt: null,
            profileId: { notIn: adminProfileIds },
          };
          const [memberCount, recentMembers] = await Promise.all([
            tx.organizerMember.count({ where: memberWhere }),
            tx.organizerMember.findMany({
              where: memberWhere,
              orderBy: { createdAt: 'desc' },
              take: 5,
              select: {
                createdAt: true,
                profile: { select: PUBLIC_PROFILE_SELECT },
              },
            }),
          ]);
          return { admins, memberCount, recentMembers };
        },
      );

      const combined = [
        ...admins.map((a) => ({ createdAt: a.createdAt, profile: a.profile })),
        ...recentMembers,
      ]
        .sort((x, y) => y.createdAt.getTime() - x.createdAt.getTime())
        .slice(0, 5);

      return OrganizerMemberSummary.parse({
        totalCount: admins.length + memberCount,
        avatars: combined.map((c) => c.profile),
      });
    },
  );

/**
 * Public list of all admins for an organizer, ordered most-recent-joined
 * first. Returns PublicProfile shape only (no email). No auth.
 */
export const getOrganizerAdmins = createServerFn({ method: 'GET' })
  .middleware([optionalAuthMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof GetOrganizerAdminsInput> => {
      const result = GetOrganizerAdminsInput.safeParse(data);
      if (!result.success) throw new Error('Invalid input');
      return result.data;
    },
  )
  .handler(
    async ({ data }): Promise<z.infer<typeof OrganizerPublicMember>[]> => {
      const db = await prismaClient();
      const admins = await db.organizerAdmin.findMany({
        where: { organizerId: data.organizerId },
        orderBy: { createdAt: 'desc' },
        select: { profile: { select: PUBLIC_PROFILE_SELECT } },
      });
      return z.array(OrganizerPublicMember).parse(admins.map((a) => a.profile));
    },
  );

/**
 * Public, paginated list of an organizer's active (`leftAt IS NULL`)
 * members, excluding profiles that are also admins. Ordered most-recent-
 * joined first. Returns PublicProfile shape only (no email). No auth.
 */
export const getOrganizerPublicMembers = createServerFn({ method: 'GET' })
  .middleware([optionalAuthMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof GetOrganizerPublicMembersInput> => {
      const result = GetOrganizerPublicMembersInput.safeParse(data);
      if (!result.success) throw new Error('Invalid input');
      return result.data;
    },
  )
  .handler(
    async ({
      data,
    }): Promise<z.infer<typeof OrganizerPublicMembersPage>> => {
      const db = await prismaClient();
      const { organizerId, page, pageSize } = data;
      const skip = (page - 1) * pageSize;

      const { members, totalCount } = await db.$transaction(async (tx) => {
        const adminProfileIds = (
          await tx.organizerAdmin.findMany({
            where: { organizerId },
            select: { profileId: true },
          })
        ).map((a) => a.profileId);
        const where = {
          organizerId,
          leftAt: null,
          profileId: { notIn: adminProfileIds },
        };
        const [members, totalCount] = await Promise.all([
          tx.organizerMember.findMany({
            where,
            orderBy: { createdAt: 'desc' },
            skip,
            take: pageSize,
            select: { profile: { select: PUBLIC_PROFILE_SELECT } },
          }),
          tx.organizerMember.count({ where }),
        ]);
        return { members, totalCount };
      });

      return OrganizerPublicMembersPage.parse({
        rows: members.map((m) => m.profile),
        page,
        pageSize,
        totalCount,
        hasNextPage: page * pageSize < totalCount,
      });
    },
  );
